import React, { useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BetaBanner from '../components/BetaBanner';
import GoogleAuthButton from '../components/GoogleAuthButton';
import EmailMagicLinkForm from '../components/EmailMagicLinkForm';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const [search] = useSearchParams();
  const reason = search.get('reason');
  const next = search.get('next');
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Stash intended-next so AuthCallback can read it after Google round-trip.
  useEffect(() => {
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      sessionStorage.setItem('post_login_redirect', next);
    }
  }, [next]);

  // If already authenticated, bounce away immediately (admins → /admin, users → /dashboard,
  // honoring an explicit `next` if present and safe).
  useEffect(() => {
    if (loading || !user) return;
    let dest;
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      dest = next.startsWith('/admin') && !user.isAdmin ? '/dashboard' : next;
    } else {
      dest = '/dashboard';
    }
    navigate(dest, { replace: true });
  }, [user, loading, next, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col">
      <BetaBanner />
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <div className="text-center">
            <p className="text-[10px] tracking-[0.25em] font-mono gold-text">BETA ACCESS</p>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
              Log in to <span className="gold-text">Talas.</span>
            </h1>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Continue with Google for one-click access, or get a one-time link emailed to any address.
            </p>
          </div>

          {reason === 'account_disabled' && (
            <div className="mt-6 border border-red-500/30 bg-red-500/[0.05] rounded-md p-4 text-xs text-red-200" data-testid="login-error-disabled">
              Your account has been disabled. Contact <a href="mailto:talassupport@gmail.com" className="underline">support</a>.
            </div>
          )}
          {reason && reason !== 'account_disabled' && (
            <div className="mt-6 border border-amber-500/30 bg-amber-500/[0.05] rounded-md p-4 text-xs text-amber-200 space-y-2" data-testid="login-error-detail">
              <p>
                Sign-in didn't complete. Please try again.
              </p>
              <p className="font-mono text-[10px] text-amber-300/80 break-all">
                detail: {reason}
              </p>
              <p className="text-[11px] text-amber-200/60">
                If this persists, copy the detail above and share it with <a href="mailto:talassupport@gmail.com" className="underline">support</a>.
              </p>
            </div>
          )}

          <div className="mt-8 border border-white/10 rounded-md p-7 bg-[#0c0c0e]">
            <GoogleAuthButton label="Continue with Google" testid="login-google-btn" />

            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <div className="flex-1 h-px bg-white/5" />
              <span className="text-[10px] tracking-[0.25em] font-mono text-zinc-600">OR</span>
              <div className="flex-1 h-px bg-white/5" />
            </div>

            <EmailMagicLinkForm source="login" next={next} ctaLabel="Email me a sign-in link" />

            <div className="mt-5 pt-5 border-t border-white/5 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
              <ShieldCheck size={12} className="gold-text mt-0.5 shrink-0" strokeWidth={2} />
              <span>Talas never stores passwords. Sign in with Google, or get a one-time link emailed to any address.</span>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-zinc-500">
            New to Talas? <Link to="/signup" className="gold-text hover:underline">Sign up free</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
